import React, { useMemo } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LabelList } from 'recharts';
import Card from '../../ui/Card.jsx';
import Legend from './Legend.jsx';
import { getActiveTheme, resolveThemeName } from '../../ui/tokens';
import { formatMinutesToHM } from '../../ui/format';

function MinutesTooltip({ active, payload, label }) {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="text-xs rounded border bg-white dark:bg-gray-900 p-2">
      <div className="font-medium mb-1">{label}</div>
      {payload.map((p,i)=>(
        <div key={i} className="flex justify-between gap-4">
          <span className="text-gray-600 dark:text-gray-300">{p.name}</span>
          <span>{formatMinutesToHM(p.value)}</span>
        </div>
      ))}
    </div>
  );
}

export default function UtilizationByProjectChart({ projects = [], topN = 8, onProjectClick, loading = false }) {
  const theme = getActiveTheme();
  const isPremiumLight = resolveThemeName() === 'auroraExecutiveLight' || resolveThemeName() === 'redLightPremium';
  const gridOpacity = Number(theme?.chartGridOpacity ?? (isPremiumLight ? 0.06 : 0.12));
  const gridStroke = isPremiumLight ? `rgba(15, 23, 42, ${gridOpacity})` : (theme?.border || '#e5e7eb');
  const axisStroke = isPremiumLight ? 'rgba(15, 23, 42, 0.14)' : (theme?.border || '#e5e7eb');
  const rows = useMemo(() => {
    const formatted = projects.map(p => ({
      name: p.project_name || '—',
      planned: Number(p.planned_minutes || 0),
      logged: Number(p.logged_total_minutes || 0),
      utilPct: (p.planned_minutes > 0) ? Math.min(100, Math.round((p.logged_total_minutes * 100) / p.planned_minutes)) : 0,
      project_id: p.project_id,
    }));
    return formatted.sort((a,b)=> b.logged - a.logged).slice(0, topN);
  }, [projects, topN]);

  return (
    <Card padded={true} className="w-full">
      <div className="mb-2 flex items-center justify-between">
        <div className="text-sm font-semibold">Utilization by Project</div>
        <Legend items={[{ label: 'Logged', color: theme.primary }, { label: 'Planned', color: theme.success }]} />
      </div>
      <div className="h-[260px]">
        {loading && (<div className="absolute inset-0 bg-black/5 dark:bg-white/5 flex items-center justify-center text-xs">Loading…</div>)}
        {(!rows || rows.length===0) ? (
          <div className="h-full flex items-center justify-center text-xs text-gray-600 dark:text-gray-400">No data for selected range</div>
        ) : (
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={rows} layout="vertical" onClick={(e)=>{
            const idx = e && e.activeTooltipIndex;
            if (typeof idx === 'number') {
              const r = rows[idx];
              if (r && onProjectClick) onProjectClick(r.project_id);
            }
          }}>
            <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} />
            <XAxis
              type="number"
              tick={isPremiumLight ? { fill: theme?.mutedText || '#6B7280', fontSize: 12 } : undefined}
              axisLine={isPremiumLight ? { stroke: axisStroke } : undefined}
              tickLine={isPremiumLight ? { stroke: axisStroke } : undefined}
            />
            <YAxis
              type="category"
              dataKey="name"
              width={140}
              tick={isPremiumLight ? { fill: theme?.mutedText || '#6B7280', fontSize: 12 } : undefined}
              axisLine={isPremiumLight ? { stroke: axisStroke } : undefined}
              tickLine={isPremiumLight ? { stroke: axisStroke } : undefined}
            />
            <Tooltip content={<MinutesTooltip />} />
            <Bar dataKey="logged" name="Logged" fill={theme.primary}>
              <LabelList dataKey={(r)=> `${r.utilPct}%`} position="right" fontSize={12} />
            </Bar>
            <Bar dataKey="planned" name="Planned" fill={theme.success} />
          </BarChart>
        </ResponsiveContainer>
        )}
      </div>
    </Card>
  );
}
